import DeleteIcon from "@mui/icons-material/Delete";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Box,
  Button,
  Collapse,
  IconButton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { round } from "../../../../utils/lodash-replacements";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { useActiveFBOs } from "../../../../containers/ActiveFBOContainer";
import { Ramp } from "../../../../types";
import {
  ConfirmationDialog,
  ConfirmationDialogProps,
} from "../../../../widgets/ConfirmationDialog";
import { MetricAwareTextField } from "../../../../widgets/MetricAwareTextField/MetricAwareTextField";
import { Toolbar } from "./Toolbar/Toolbar";
import { calculateRampBoundingBox } from "./calculateOrigin";

type Props = {
  ramp: Ramp;
  setRamp: (rampOrUpdater: Ramp | ((prevRamp: Ramp) => Ramp)) => void;
  onClickSaveRamp: () => void;
  onClose: () => void;
  selectedTool: string;
  setSelectedTool: (tool: string) => void;
};

export const RampEditorForm: React.FC<Props> = ({
  ramp,
  setRamp,
  onClickSaveRamp,
  onClose,
  selectedTool,
  setSelectedTool,
}) => {
  const { t } = useTranslation();
  const { activeFBO } = useActiveFBOs();
  const [confirmSave, setConfirmSave] =
    React.useState<ConfirmationDialogProps>(null);

  // Ramp details collapse state with localStorage persistence
  const [rampDetailsExpanded, setRampDetailsExpanded] = React.useState<boolean>(
    () => {
      const saved = localStorage.getItem("ramp-editor-ramp-details-expanded");
      return saved !== null ? JSON.parse(saved) : true;
    }
  );

  // Save ramp details expanded state to localStorage
  React.useEffect(() => {
    localStorage.setItem(
      "ramp-editor-ramp-details-expanded",
      JSON.stringify(rampDetailsExpanded)
    );
  }, [rampDetailsExpanded]);

  const handleClose = () => {
    onClose();
  };

  const boundingBox = calculateRampBoundingBox(ramp);
  const unit = activeFBO?.settings?.useMetricSystem ? "m" : "ft";

  return (
    <Stack
      direction="column"
      spacing={0.5}
      sx={{
        p: 1,
        height: "100%",
      }}
    >
      {/* Ramp Details Section */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          cursor: "pointer",
          "&:hover": { backgroundColor: "action.hover" },
          borderRadius: 1,
          px: 0.5,
          py: 0.25,
        }}
        onClick={() => setRampDetailsExpanded(!rampDetailsExpanded)}
      >
        <Typography variant="subtitle2">{ramp?.name || t('settings.rampsSettings.rampEditorForm.editRamp')}</Typography>
        {rampDetailsExpanded ? (
          <ExpandLessIcon fontSize="small" />
        ) : (
          <ExpandMoreIcon fontSize="small" />
        )}
      </Box>

      <Collapse in={rampDetailsExpanded} sx={{ width: "100%" }}>
        <Stack direction="column" spacing={1}>
          <TextField
            fullWidth
            size="small"
            label={t('settings.rampsSettings.labels.rampName')}
            onChange={(evt) => {
              setRamp((currentRamp) => ({
                ...currentRamp,
                name: evt.target.value,
              }));
            }}
            value={ramp?.name ?? ""}
          />
          <Stack direction="column" spacing={1}>
            <MetricAwareTextField
              unit={activeFBO?.settings?.useMetricSystem ? "meters" : "feet"}
              type="number"
              fullWidth
              disabled
              InputProps={{ inputProps: { min: 1, max: 10000 } }}
              size="small"
              label={t('settings.rampsSettings.labels.width', { unit })}
              onChange={(value) => {
                setRamp((currentRamp) => ({
                  ...currentRamp,
                  width: value,
                }));
              }}
              value={round(ramp?.width, 0)}
            />
            <MetricAwareTextField
              unit={activeFBO?.settings?.useMetricSystem ? "meters" : "feet"}
              type="number"
              disabled
              fullWidth
              InputProps={{ inputProps: { min: 1, max: 10000 } }}
              size="small"
              label={t('settings.rampsSettings.labels.depth', { unit })}
              onChange={(value) => {
                setRamp((currentRamp) => ({
                  ...currentRamp,
                  depth: value,
                }));
              }}
              value={round(ramp?.depth, 0)}
            />
          </Stack>
          <Stack direction="column" spacing={1}>
            <Typography variant="subtitle2">
              {t('settings.rampsSettings.rampEditorForm.boundingBoxCoordinates')}
            </Typography>
            {boundingBox ? (
              <Stack direction="column" spacing={0.25}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography
                    variant="caption"
                    sx={{
                      minWidth: 80,
                      fontWeight: "medium",
                      fontSize: "0.7rem",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {t('settings.rampsSettings.rampEditorForm.coordinates.topLeft')}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      fontFamily: "monospace",
                      backgroundColor: "grey.100",
                      px: 0.5,
                      py: 0.25,
                      borderRadius: 0.5,
                      fontSize: "0.7rem",
                    }}
                  >
                    {boundingBox.topLeft[0]}, {boundingBox.topLeft[1]}
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography
                    variant="caption"
                    sx={{
                      minWidth: 80,
                      fontWeight: "medium",
                      fontSize: "0.7rem",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {t('settings.rampsSettings.rampEditorForm.coordinates.topRight')}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      fontFamily: "monospace",
                      backgroundColor: "grey.100",
                      px: 0.5,
                      py: 0.25,
                      borderRadius: 0.5,
                      fontSize: "0.7rem",
                    }}
                  >
                    {boundingBox.topRight[0]}, {boundingBox.topRight[1]}
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography
                    variant="caption"
                    sx={{
                      minWidth: 80,
                      fontWeight: "medium",
                      fontSize: "0.7rem",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {t('settings.rampsSettings.rampEditorForm.coordinates.bottomLeft')}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      fontFamily: "monospace",
                      backgroundColor: "grey.100",
                      px: 0.5,
                      py: 0.25,
                      borderRadius: 0.5,
                      fontSize: "0.7rem",
                    }}
                  >
                    {boundingBox.bottomLeft[0]}, {boundingBox.bottomLeft[1]}
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography
                    variant="caption"
                    sx={{
                      minWidth: 80,
                      fontWeight: "medium",
                      fontSize: "0.7rem",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {t('settings.rampsSettings.rampEditorForm.coordinates.bottomRight')}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      fontFamily: "monospace",
                      backgroundColor: "grey.100",
                      px: 0.5,
                      py: 0.25,
                      borderRadius: 0.5,
                      fontSize: "0.7rem",
                    }}
                  >
                    {boundingBox.bottomRight[0]}, {boundingBox.bottomRight[1]}
                  </Typography>
                </Box>
              </Stack>
            ) : (
              <Typography variant="body2" color="text.secondary">
                {t('settings.rampsSettings.rampEditorForm.messages.needCalibrationPoints')}
              </Typography>
            )}
          </Stack>
        </Stack>
      </Collapse>

      <Stack direction="column" spacing={1}>
        {ramp?.markings?.map((marking, idx) => (
          <Stack
            key={`marking-${idx}`}
            direction="row"
            spacing={1}
            alignItems="center"
            onMouseEnter={() => {
              setRamp((currentRamp) => ({
                ...currentRamp,
                markings: currentRamp.markings.map((m) => ({
                  ...m,
                  selected: m.id === marking.id,
                })),
              }));
            }}
            onMouseLeave={() => {
              setRamp((currentRamp) => ({
                ...currentRamp,
                markings: currentRamp.markings.map((m) => ({
                  ...m,
                  selected: false,
                })),
              }));
            }}
          >
            <Typography>{marking.type}</Typography>
            <IconButton
              onClick={() => {
                setRamp((currentRamp) => ({
                  ...currentRamp,
                  markings: currentRamp.markings.filter((m, i) => i !== idx),
                }));
              }}
            >
              <DeleteIcon />
            </IconButton>
          </Stack>
        ))}
      </Stack>
      <Toolbar selectedTool={selectedTool} setSelectedTool={setSelectedTool} />
      <Stack direction="row" spacing={1} alignItems="flex-start">
        <Button
          fullWidth
          size="small"
          variant="contained"
          color="inherit"
          onClick={handleClose}
        >
          {t('common.cancel')}
        </Button>
        <Button
          fullWidth
          size="small"
          variant="contained"
          color="success"
          onClick={() => {
            setConfirmSave({
              title: t('settings.rampsSettings.rampEditorForm.confirmations.saveChanges.title'),
              text: t('settings.rampsSettings.rampEditorForm.confirmations.saveChanges.message'),
              onCancel: () => setConfirmSave(null),
              onClose: () => setConfirmSave(null),
              okText: t('common.save'),
              onOk: () => {
                onClickSaveRamp();
                setConfirmSave(null);
              },
            });
          }}
        >
          {t('common.save')}
        </Button>
      </Stack>
      {Boolean(confirmSave) && <ConfirmationDialog {...confirmSave} />}
    </Stack>
  );
};
